/**
 * Everything the grown-up screen shows, worked out from a profile.
 *
 * Pure: no Phaser. The ParentStats scene only draws what these return, so the
 * numbers a parent reads can be tested without a canvas.
 *
 * Chart colours follow the data-viz method: each chart is a single series, so
 * the title names it and there is no legend; the key heatmap is one hue,
 * stepped dark to light, with the steps validated against the panel surface.
 */

import type { ProfileSlot, RunEntry } from './SaveManager';

/* ------------------------------------------------------------------ colour */

/** The panel the charts sit on. The validator was run against this exact colour. */
export const PANEL_SURFACE = 0x141f33;

/**
 * The line colour for both charts.
 *
 * The brand teal (#2EC4B6) is too light for a dark surface (OKLCH L 0.74,
 * outside the 0.48-0.67 band), so this is the same hue stepped down until it
 * passes. One colour for both charts: each is a single series named by its
 * title, so colour is not being asked to carry identity.
 */
export const SERIES_COLOR = 0x22a99c;

/**
 * Heat tiers for missed keys, fewest to most.
 *
 * Steps 600 -> 150 of the reference blue ramp, validated as an ordinal ramp
 * on PANEL_SURFACE: one hue, lightness rising monotonically, every gap
 * visible, and the darkest step still clear of the panel. On a dark surface
 * "more" is brighter, so the keys that need practice are the ones that pop.
 */
export const HEAT_TIERS: readonly number[] = [0x184f95, 0x256abf, 0x3987e5, 0x6da7ec, 0xb7d3f6];

/** A key that has never been missed: a plain key cap, not the bottom of the ramp. */
export const KEY_IDLE = 0x22314d;

/* --------------------------------------------------------------- keyboard */

/** The letter rows of a US keyboard, with each row's indent in key widths. */
export const KEYBOARD_ROWS: ReadonlyArray<{ readonly keys: string; readonly indent: number }> = [
  { keys: 'qwertyuiop', indent: 0 },
  { keys: 'asdfghjkl', indent: 0.35 },
  { keys: 'zxcvbnm', indent: 0.85 },
];

/**
 * Miss counts per key cap.
 *
 * Folded to lower case: "A" and "a" are the same key, and a parent asking
 * "which keys does she miss" means the cap, not the case. Anything that is not
 * a letter is dropped, because the heatmap only draws letters.
 */
export function keyCounts(missedKeys: Readonly<Record<string, number>>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, count] of Object.entries(missedKeys)) {
    const cap = key.toLowerCase();
    if (!/^[a-z]$/.test(cap) || count <= 0) continue;
    out[cap] = (out[cap] ?? 0) + count;
  }
  return out;
}

/**
 * Which heat tier a count falls in: -1 for never missed, else 0..4.
 *
 * Relative to this player's worst key rather than to a fixed scale, so the
 * keyboard always shows where to practise next even for a strong typist with
 * only a handful of misses in total.
 */
export function heatTier(count: number, max: number): number {
  if (count <= 0 || max <= 0) return -1;
  const share = Math.min(1, count / max);
  return Math.min(HEAT_TIERS.length - 1, Math.floor(share * HEAT_TIERS.length - 1e-9));
}

/* ------------------------------------------------------------ missed words */

export interface MissedWord {
  readonly word: string;
  readonly count: number;
}

/** The most-missed words, most first; ties alphabetical so the list is stable. */
export function topMissedWords(
  missedWords: Readonly<Record<string, number>>,
  limit = 20,
): MissedWord[] {
  return Object.entries(missedWords)
    .filter(([, count]) => count > 0)
    .sort(([a, ca], [b, cb]) => cb - ca || a.localeCompare(b))
    .slice(0, limit)
    .map(([word, count]) => ({ word, count }));
}

/* ------------------------------------------------------------------ charts */

export type ChartField = 'wpm' | 'accuracy';

/** How many recent runs a chart shows; older runs are summarised, not plotted. */
export const CHART_RUNS = 30;

/** The last `limit` values of one measure, oldest first, ready to plot. */
export function recentSeries(
  history: readonly RunEntry[],
  field: ChartField,
  limit = CHART_RUNS,
): number[] {
  return history.slice(-limit).map((run) => run[field]);
}

/**
 * A round number at or above `value`, for the top of a chart's y axis.
 *
 * Steps through 1, 2, 2.5 and 5 per decade, so a peak of 23 WPM gets an axis
 * to 25 rather than to an awkward 23 or a wasteful 50.
 */
export function niceAxisMax(value: number): number {
  if (!(value > 0)) return 10;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  for (const step of [1, 2, 2.5, 5, 10]) {
    const candidate = step * magnitude;
    if (candidate >= value) return candidate;
  }
  return 10 * magnitude;
}

/**
 * Round tick values covering [low, high], about `target` intervals apart.
 *
 * Splitting a range into a fixed number of equal parts gives ticks like 70,
 * 78, 85, 93, 100. Choosing a round step first (1, 2, 2.5, 5 or 10 per
 * decade) and widening the range to whole steps gives 70, 80, 90, 100.
 */
export function niceTicks(low: number, high: number, target = 4): number[] {
  const span = Math.max(high - low, 1e-6);
  const raw = span / Math.max(1, target);
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const step =
    [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((candidate) => candidate >= raw) ??
    10 * magnitude;

  const first = Math.floor(low / step + 1e-9) * step;
  const last = Math.ceil(high / step - 1e-9) * step;
  const ticks: number[] = [];
  for (let value = first; value <= last + step / 2; value += step) {
    ticks.push(Math.round(value * 1000) / 1000);
  }
  return ticks;
}

/* ----------------------------------------------------------------- summary */

export interface ProfileSummary {
  readonly runs: number;
  readonly levelsCompleted: number;
  readonly stars: number;
  readonly minutesPlayed: number;
  /** Mean WPM over the last few runs; 0 with no history. */
  readonly recentWpm: number;
  /** Mean accuracy over the last few runs; 0 with no history. */
  readonly recentAccuracy: number;
  readonly bestWpm: number;
  /**
   * Recent WPM minus the WPM of the runs before them, or null when there are
   * not yet enough runs to compare honestly.
   */
  readonly wpmChange: number | null;
}

/** How many recent runs the summary averages, and the window it compares to. */
export const SUMMARY_WINDOW = 5;

function mean(values: readonly number[]): number {
  return values.length === 0 ? 0 : values.reduce((sum, v) => sum + v, 0) / values.length;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

export function summarise(profile: ProfileSlot): ProfileSummary {
  const history = profile.history;
  const recent = history.slice(-SUMMARY_WINDOW);
  const before = history.slice(-SUMMARY_WINDOW * 2, -SUMMARY_WINDOW);

  return {
    runs: history.length,
    levelsCompleted: profile.totals.levelsCompleted,
    stars: profile.totals.stars,
    minutesPlayed: Math.round(profile.totals.playMs / 60000),
    recentWpm: round1(mean(recent.map((run) => run.wpm))),
    recentAccuracy: round1(mean(recent.map((run) => run.accuracy))),
    bestWpm: round1(history.reduce((best, run) => Math.max(best, run.wpm), 0)),
    // Only compare a full window against a full window: two runs against
    // three is noise dressed up as a trend.
    wpmChange:
      before.length === SUMMARY_WINDOW
        ? round1(mean(recent.map((r) => r.wpm)) - mean(before.map((r) => r.wpm)))
        : null,
  };
}

/* -------------------------------------------------------------------- gate */

export interface GateQuestion {
  readonly prompt: string;
  readonly answer: number;
}

/**
 * A two-digit times one-digit sum, like "17 x 6".
 *
 * A speed bump, not security: quick for an adult, slow enough that a young
 * player wandering through menus does not end up in the stats by accident.
 * The operands avoid x1, x2, x10 and x11 patterns that are guessable at a
 * glance.
 */
export function makeGateQuestion(random: () => number): GateQuestion {
  const a = 12 + Math.floor(random() * 18); // 12..29
  const b = 3 + Math.floor(random() * 7); // 3..9
  return { prompt: `What is ${a} × ${b}?`, answer: a * b };
}

/** Whether what was typed is the answer; spaces ignored, anything else must match. */
export function isGateAnswer(typed: string, question: GateQuestion): boolean {
  const cleaned = typed.trim();
  if (!/^\d+$/.test(cleaned)) return false;
  return Number.parseInt(cleaned, 10) === question.answer;
}
